<template>
  <div class="qfl">
    <div class="qfl-header">
      <p class="qfl-subtitle">
        Kimga xabar ketdi, qaysi kanal orqali va nima uchun ketmadi.
      </p>
      <el-button :loading="loading" @click="refresh">
        <el-icon style="margin-right: 4px"><Refresh /></el-icon>
        Yangilash
      </el-button>
    </div>

    <div class="qfl-summary">
      <button
        v-for="tile in summaryTiles"
        :key="tile.value || 'all'"
        type="button"
        class="qfl-tile"
        :class="{ 'qfl-tile--active': status === tile.value }"
        @click="selectStatus(tile.value)"
      >
        <span class="qfl-tile__label">{{ tile.label }}</span>
        <span class="qfl-tile__value" :class="`qfl-tile__value--${tile.tag}`">
          <el-icon v-if="summaryLoading" class="is-loading"><Loading /></el-icon>
          <template v-else>{{ formatCount(summary[tile.value || "total"]) }}</template>
        </span>
      </button>
    </div>

    <div class="qfl-filters">
      <el-select
        v-model="regionId"
        placeholder="Viloyat"
        clearable
        filterable
        :loading="regionsLoading"
        class="qfl-filters__select"
        @change="onRegionChange"
      >
        <el-option
          v-for="region in regions"
          :key="region.id"
          :value="region.id"
          :label="region.title"
        />
      </el-select>

      <el-select
        v-model="districtId"
        placeholder="Tuman"
        clearable
        filterable
        :loading="districtsLoading"
        class="qfl-filters__select"
        @change="onDistrictChange"
      >
        <el-option
          v-for="district in districts"
          :key="district.id"
          :value="district.id"
          :label="district.title"
        />
      </el-select>

      <el-select
        v-model="quarterId"
        :placeholder="districtId ? 'Mahalla' : 'Avval tumanni tanlang'"
        :disabled="!districtId"
        clearable
        filterable
        :loading="quartersLoading"
        class="qfl-filters__select"
        @change="onFilterChange"
      >
        <el-option
          v-for="quarter in quarters"
          :key="quarter.id"
          :value="quarter.id"
          :label="quarter.title"
        />
      </el-select>

      <el-select
        v-model="eventType"
        placeholder="Hodisa turi"
        clearable
        class="qfl-filters__select qfl-filters__select--sm"
        @change="onFilterChange"
      >
        <el-option
          v-for="event in eventTypes"
          :key="event.value"
          :value="event.value"
          :label="event.label"
        />
      </el-select>

      <el-select
        v-model="status"
        placeholder="Holat"
        clearable
        class="qfl-filters__select qfl-filters__select--sm"
        @change="onFilterChange"
      >
        <el-option
          v-for="item in statuses"
          :key="item.value"
          :value="item.value"
          :label="item.label"
        />
      </el-select>

      <el-button v-if="hasActiveFilters" @click="resetFilters">Tozalash</el-button>
    </div>

    <div class="qfl-note">
      <el-icon><InfoFilled /></el-icon>
      <span>
        Kanal holati "ketdi" — xabar navbatga qo'yilgani, provayder yetkazgani
        emas. Provayder darajasidagi holat SMS statistikasida ko'rinadi.
      </span>
    </div>

    <div v-if="errorMessage" class="qfl-state">
      <el-result icon="error" title="Jurnalni yuklab bo'lmadi" :sub-title="errorMessage">
        <template #extra>
          <el-button type="primary" @click="fetchNotifications">
            <el-icon style="margin-right: 4px"><Refresh /></el-icon>
            Qayta urinish
          </el-button>
        </template>
      </el-result>
    </div>

    <div v-else-if="loading" class="qfl-state">
      <el-skeleton :rows="8" animated />
    </div>

    <div v-else-if="!notifications.length" class="qfl-state">
      <MfEmpty
        title="Yozuv topilmadi"
        :description="
          hasActiveFilters
            ? 'Filtrlarni o\'zgartirib ko\'ring'
            : 'Hali birorta bildirishnoma yuborilmagan. To\'lovlar bo\'lgani aniq bo\'lsa — backend jamoasiga murojaat qiling'
        "
      />
      <div v-if="hasActiveFilters" class="qfl-state__actions">
        <el-button @click="resetFilters">Filtrlarni tozalash</el-button>
      </div>
    </div>

    <template v-else>
      <el-table :data="notifications" border stripe class="qfl-table">
        <el-table-column type="expand">
          <template #default="props">
            <div class="qfl-detail">
              <div class="qfl-detail__item">
                <b>Foydalanuvchi ID:</b>
                <span class="qfl-mono">{{ props.row.user_id || "—" }}</span>
              </div>
              <div class="qfl-detail__item">
                <b>Telefon:</b> {{ phone(props.row.phone_number) }}
              </div>
              <div class="qfl-detail__item">
                <b>Urinishlar soni:</b> {{ props.row.attempt_count ?? "—" }}
              </div>
              <div class="qfl-detail__item">
                <b>{{ sentAtLabel(props.row.status) }}:</b>
                {{ formatDate(props.row.sent_at) }}
              </div>
              <div class="qfl-detail__item">
                <b>Hudud:</b>
                {{ [props.row.region_title, props.row.district_title, props.row.quarter_title]
                  .filter(Boolean).join(" · ") || "—" }}
              </div>
              <div v-if="props.row.last_error" class="qfl-detail__item qfl-detail__item--full">
                <b>Sabab:</b> {{ explain(props.row.last_error) }}
                <div class="qfl-detail__raw">{{ props.row.last_error }}</div>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="Vaqt" width="150">
          <template #default="scope">{{ formatDate(scope.row.created_at) }}</template>
        </el-table-column>
        <el-table-column label="Foydalanuvchi" min-width="190">
          <template #default="scope">
            <div>{{ scope.row.fullname || "—" }}</div>
            <div class="qfl-muted">{{ phone(scope.row.phone_number) }}</div>
          </template>
        </el-table-column>
        <el-table-column label="Hodisa" width="140" align="center">
          <template #default="scope">
            <el-tag size="small" type="info" effect="plain">
              {{ eventLabel(scope.row.event_type) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="Mahalla" min-width="160">
          <template #default="scope">
            <div>{{ scope.row.quarter_title || "—" }}</div>
            <div class="qfl-muted">{{ scope.row.district_title || "" }}</div>
          </template>
        </el-table-column>
        <el-table-column label="Summa, so'm" width="140" align="right">
          <template #default="scope">{{ som(scope.row.amount) }}</template>
        </el-table-column>
        <el-table-column label="Qoldiq, so'm" width="140" align="right">
          <template #default="scope">{{ som(scope.row.balance_after) }}</template>
        </el-table-column>
        <el-table-column label="Holat" width="120" align="center">
          <template #default="scope">
            <el-tooltip
              placement="top"
              :content="statusOf(scope.row.status).hint"
              :disabled="!statusOf(scope.row.status).hint"
            >
              <el-tag size="small" :type="statusOf(scope.row.status).tag" effect="light">
                {{ statusOf(scope.row.status).label }}
              </el-tag>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column label="Urinish" width="100" align="center">
          <template #default="scope">
            <el-tooltip
              v-if="scope.row.attempt_count > 1"
              placement="top"
              content="Bir necha marta urinilgan — xabar takror yuborilgan bo'lishi mumkin"
            >
              <el-tag size="small" type="warning" effect="plain">
                {{ scope.row.attempt_count }}×
              </el-tag>
            </el-tooltip>
            <span v-else>{{ scope.row.attempt_count ?? "—" }}</span>
          </template>
        </el-table-column>
        <el-table-column label="Kanallar" width="180">
          <template #default="scope">
            <div v-if="channelList(scope.row).length" class="qfl-channels">
              <el-tooltip
                v-for="channel in channelList(scope.row)"
                :key="channel.key"
                placement="top"
                :content="channel.hint"
                :disabled="!channel.hint"
              >
                <el-tag size="small" effect="plain" :type="channel.tag">
                  {{ channel.label }}: {{ channel.statusLabel }}
                </el-tag>
              </el-tooltip>
            </div>
            <span v-else class="qfl-muted">—</span>
          </template>
        </el-table-column>
      </el-table>
    </template>

    <el-pagination
      v-if="!errorMessage && count > 0"
      background
      layout="sizes, prev, pager, next, total"
      :total="count"
      :page-sizes="[20, 50, 100, 200]"
      v-model:page-size="limit"
      v-model:current-page="page"
      class="qfl-pagination"
      @size-change="onSizeChange"
      @current-change="scheduleListFetch"
    />
  </div>
</template>

<script>
import { ElMessage } from "element-plus";
import { InfoFilled, Loading, Refresh } from "@element-plus/icons-vue";
import MfEmpty from "@/components/core/MfEmpty.vue";
import api, {
  CHANNELS,
  DEFAULT_LIMIT,
  EVENT_TYPES,
  NOTIFICATION_STATUSES,
  apiErrorMessage,
  channelStatusMeta,
  eventTypeLabel,
  explainLastError,
  formatPhone,
  formatSom,
  loadDistricts,
  loadQuarters,
  loadRegions,
  statusMeta,
} from "./quarterFundNotificationsApi";

const emptySummary = () =>
  NOTIFICATION_STATUSES.reduce((acc, item) => ({ ...acc, [item.value]: 0 }), {
    total: 0,
  });

const SENT_AT_LABELS = {
  sent: "Yuborilgan vaqt",
  failed: "Oxirgi urinish",
  skipped: "O'tkazib yuborilgan vaqt",
  pending: "Yuborilgan vaqt",
};

export default {
  name: "QuarterFundNotificationLogs",
  components: { MfEmpty, InfoFilled, Loading, Refresh },
  data() {
    return {
      loading: true,
      errorMessage: "",
      notifications: [],
      count: 0,
      page: 1,
      limit: DEFAULT_LIMIT,
      // Eskirgan javob yangisini bosib ketmasligi uchun.
      listRequestId: 0,
      fetchQueued: false,

      // Backend yig'ma endpoint bermaydi — har holat uchun `count` alohida olinadi.
      summaryLoading: false,
      summaryRequestId: 0,
      summary: emptySummary(),

      regions: [],
      districts: [],
      quarters: [],
      regionsLoading: false,
      districtsLoading: false,
      quartersLoading: false,

      regionId: null,
      districtId: null,
      quarterId: null,
      eventType: "",
      status: "",

      eventTypes: EVENT_TYPES,
      statuses: NOTIFICATION_STATUSES,
    };
  },
  computed: {
    summaryTiles() {
      return [{ value: "", label: "Jami", tag: "default" }, ...NOTIFICATION_STATUSES];
    },
    hasActiveFilters() {
      return !!(
        this.regionId ||
        this.districtId ||
        this.quarterId ||
        this.eventType ||
        this.status
      );
    },
  },
  mounted() {
    this.$store.commit("updateHeaderTitle", "Yuborish jurnali");
    this.fetchRegions();
    this.fetchNotifications();
    this.fetchSummary();
  },
  methods: {
    phone(value) {
      return formatPhone(value);
    },
    som(value) {
      return formatSom(value);
    },
    eventLabel(value) {
      return eventTypeLabel(value);
    },
    statusOf(value) {
      return statusMeta(value);
    },
    explain(value) {
      return explainLastError(value);
    },
    sentAtLabel(status) {
      return SENT_AT_LABELS[status] || "Vaqt";
    },
    formatDate(value) {
      if (!value) return "—";
      return this.$moment(value).format("DD.MM.YYYY HH:mm");
    },
    formatCount(value) {
      if (value === null || value === undefined) return "—";
      return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    },
    // Kalit `channels` da bo'lmasa — fuqaro bu kanalni yoqmagan, ko'rsatmaymiz.
    channelList(row) {
      const channels = row?.channels || {};
      return CHANNELS.filter((channel) => channels[channel.key]).map((channel) => {
        const meta = channelStatusMeta(channel.key, channels[channel.key]);
        return {
          ...channel,
          statusLabel: meta.label,
          tag: meta.tag,
          hint: meta.hint,
        };
      });
    },

    // ---- hudud filtrlari ----
    fetchRegions() {
      this.regionsLoading = true;
      loadRegions()
        .then((regions) => {
          this.regions = regions;
        })
        .catch(() => {
          ElMessage.warning("Viloyatlar ro'yxatini yuklab bo'lmadi");
        })
        .finally(() => {
          this.regionsLoading = false;
        });
    },
    fetchDistricts() {
      this.districtsLoading = true;
      loadDistricts(this.regionId)
        .then((districts) => {
          this.districts = districts;
        })
        .catch(() => {
          ElMessage.warning("Tumanlar ro'yxatini yuklab bo'lmadi");
        })
        .finally(() => {
          this.districtsLoading = false;
        });
    },
    fetchQuarters() {
      this.quartersLoading = true;
      loadQuarters({ regionId: this.regionId, districtId: this.districtId })
        .then((quarters) => {
          this.quarters = quarters;
        })
        .catch(() => {
          ElMessage.warning("Mahallalar ro'yxatini yuklab bo'lmadi");
        })
        .finally(() => {
          this.quartersLoading = false;
        });
    },
    onRegionChange() {
      this.districtId = null;
      this.quarterId = null;
      this.districts = [];
      this.quarters = [];
      if (this.regionId) this.fetchDistricts();
      this.onFilterChange();
    },
    onDistrictChange() {
      this.quarterId = null;
      this.quarters = [];
      if (this.districtId) this.fetchQuarters();
      this.onFilterChange();
    },
    selectStatus(value) {
      this.status = this.status === value ? "" : value;
      this.onFilterChange();
    },
    // `clearable` select tozalanganda `undefined` beradi — plitkalar bilan
    // solishtirish uchun bo'sh satrga keltiriladi.
    onFilterChange() {
      this.eventType = this.eventType || "";
      this.status = this.status || "";
      this.page = 1;
      this.fetchNotifications();
      this.fetchSummary();
    },
    resetFilters() {
      this.regionId = null;
      this.districtId = null;
      this.quarterId = null;
      this.districts = [];
      this.quarters = [];
      this.eventType = "";
      this.status = "";
      this.onFilterChange();
    },
    onSizeChange() {
      this.page = 1;
      this.scheduleListFetch();
    },
    // Hajm o'zgarganda el-pagination `size-change` dan keyin `current-change`
    // ni ham emit qilishi mumkin — ikkala hodisa bitta so'rovga yig'iladi.
    scheduleListFetch() {
      if (this.fetchQueued) return;
      this.fetchQueued = true;
      this.$nextTick(() => {
        this.fetchQueued = false;
        this.fetchNotifications();
      });
    },
    refresh() {
      this.fetchNotifications();
      this.fetchSummary();
    },

    // ---- ro'yxat ----
    buildFilterParams(includeStatus = true) {
      const params = {};
      if (this.regionId) params.region_id = this.regionId;
      if (this.districtId) params.district_id = this.districtId;
      if (this.quarterId) params.quarter_id = this.quarterId;
      if (this.eventType) params.event_type = this.eventType;
      if (includeStatus && this.status) params.status = this.status;
      return params;
    },
    fetchNotifications() {
      const requestId = ++this.listRequestId;
      const isStale = () => requestId !== this.listRequestId;
      this.loading = true;
      this.errorMessage = "";
      return api
        .notifications({
          ...this.buildFilterParams(),
          limit: this.limit,
          offset: this.limit * (this.page - 1),
        })
        .then(({ data }) => {
          if (isStale()) return;
          this.notifications = data?.notifications || [];
          this.count = data?.count || 0;
          this.errorMessage = "";
        })
        .catch(async (error) => {
          const message = await apiErrorMessage(error);
          if (isStale()) return;
          this.errorMessage = message;
        })
        .finally(() => {
          if (!isStale()) this.loading = false;
        });
    },
    // Yig'ma raqamlar status filtridan mustaqil. Har bir son alohida so'rov:
    // bittasi yiqilsa faqat o'sha plitka `—` bo'ladi.
    fetchSummary() {
      const requestId = ++this.summaryRequestId;
      const base = { ...this.buildFilterParams(false), limit: 1, offset: 0 };
      const count = (params) =>
        api
          .notifications(params)
          .then(({ data }) => data?.count || 0)
          .catch(() => null);
      this.summaryLoading = true;
      Promise.all([
        count(base),
        ...NOTIFICATION_STATUSES.map((item) => count({ ...base, status: item.value })),
      ])
        .then(([total, ...rest]) => {
          if (requestId !== this.summaryRequestId) return;
          const summary = { total };
          NOTIFICATION_STATUSES.forEach((item, index) => {
            summary[item.value] = rest[index];
          });
          this.summary = summary;
        })
        .finally(() => {
          if (requestId === this.summaryRequestId) this.summaryLoading = false;
        });
    },
  },
};
</script>

<style scoped>
.qfl {
  padding: 4px 0;
}
.qfl-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.qfl-subtitle {
  margin: 0;
  max-width: 720px;
  font-size: 13px;
  line-height: 1.5;
  color: #909399;
}
.qfl-summary {
  display: flex;
  gap: 12px;
  margin: 16px 0;
  flex-wrap: wrap;
}
.qfl-tile {
  flex: 1 1 150px;
  padding: 12px 16px;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  background: #fff;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.qfl-tile:hover {
  border-color: #c6e2ff;
}
.qfl-tile--active {
  border-color: #409eff;
  box-shadow: 0 0 0 1px #409eff inset;
}
.qfl-tile__label {
  display: block;
  font-size: 12px;
  color: #909399;
}
.qfl-tile__value {
  display: block;
  margin-top: 4px;
  font-size: 20px;
  font-weight: 600;
  color: #303133;
}
.qfl-tile__value--success {
  color: #67c23a;
}
.qfl-tile__value--warning {
  color: #e6a23c;
}
.qfl-tile__value--danger {
  color: #f56c6c;
}
.qfl-tile__value--info {
  color: #909399;
}
.qfl-filters {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}
.qfl-filters__select {
  width: 200px;
}
.qfl-filters__select--sm {
  width: 160px;
}
.qfl-note {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 14px;
  font-size: 12px;
  color: #909399;
}
.qfl-state {
  padding: 12px 0;
}
.qfl-state__actions {
  text-align: center;
}
.qfl-muted {
  font-size: 12px;
  color: #909399;
}
.qfl-mono {
  font-family: monospace;
  font-size: 12px;
}
.qfl-channels {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
}
.qfl-detail {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 8px 20px;
  padding: 12px 20px;
  font-size: 13px;
  color: #606266;
  background: #fafcff;
}
.qfl-detail__item--full {
  grid-column: 1 / -1;
}
.qfl-detail__raw {
  margin-top: 4px;
  font-family: monospace;
  font-size: 11px;
  color: #a8abb2;
  word-break: break-word;
}
.qfl-pagination {
  margin-top: 16px;
  justify-content: flex-end;
}

@media (max-width: 768px) {
  .qfl-filters__select,
  .qfl-filters__select--sm {
    width: 100%;
  }
}
</style>
